<template>
  <v-container fluid class="pa-6 product-search-admin-view">
    <page-bar
      title="Product Search Admin"
      subtitle="Rebuild and test the product search index for your tenant"
    >
      <template v-slot:actions>
        <v-btn small color="primary" dark :loading="reindexing" @click="onIndexClick">
          <v-icon left>mdi-database-sync</v-icon>
          Index
        </v-btn>
      </template>
    </page-bar>

    <v-row class="mt-4" dense>
      <v-col cols="12" md="4">
        <v-sheet rounded class="pa-3 stat-tile">
          <div class="caption grey--text text--darken-1">Documents in Index</div>
          <div class="subtitle-1 font-weight-bold">{{ documentCountLabel }}</div>
        </v-sheet>
      </v-col>
      <v-col cols="12" md="4">
        <v-sheet rounded class="pa-3 stat-tile">
          <div class="caption grey--text text--darken-1">Index Size</div>
          <div class="subtitle-1 font-weight-bold">{{ indexSizeLabel }}</div>
        </v-sheet>
      </v-col>
      <v-col cols="12" md="4">
        <v-sheet rounded class="pa-3 stat-tile d-flex align-center justify-center">
          <v-btn text small color="primary" :loading="statsLoading" @click="GetIndexStats">
            <v-icon left small>mdi-refresh</v-icon>
            Refresh
          </v-btn>
        </v-sheet>
      </v-col>
    </v-row>

    <v-row class="mt-2">
      <v-col cols="12">
        <v-alert v-if="statsErrorMessage" type="error" outlined dense dismissible @input="clearStatsError">
          {{ statsErrorMessage }}
        </v-alert>
      </v-col>
    </v-row>

    <v-row class="mt-2">
      <v-col cols="12">
        <v-card>
          <v-card-text>
            <v-alert v-if="errorMessage" type="error" outlined dense dismissible @input="clearError">
              {{ errorMessage }}
            </v-alert>

            <v-alert v-if="successMessage" type="success" outlined dense dismissible @input="clearSuccess">
              {{ successMessage }}
            </v-alert>

            <p class="body-2 grey--text text--darken-1">
              Rebuilds the search index for every asset in your tenant. New, updated, and deleted assets
              are kept in sync automatically as you work — use this only to seed the index the first
              time, or to recover after a search configuration change.
            </p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mt-2">
      <v-col cols="12">
        <v-card>
          <v-card-text>
            <div class="subtitle-1 font-weight-medium">Test Search</div>
            <div class="caption grey--text text--darken-1 mb-3">
              Confirms the index is populated and query-able after a reindex — not a full feature UI.
            </div>

            <v-row align="center" dense>
              <v-col cols="12" md="7">
                <v-text-field
                  v-model="searchQuery"
                  dense
                  outlined
                  clearable
                  hide-details
                  label="Search query"
                  prepend-inner-icon="mdi-magnify"
                  @keyup.enter="onSearchClick"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="5">
                <v-btn small color="primary" dark :loading="searching" @click="onSearchClick">
                  <v-icon left small>mdi-magnify</v-icon>
                  Search
                </v-btn>
                <v-btn small text class="ml-2" :disabled="searching" @click="onClearClick">
                  <v-icon left small>mdi-close</v-icon>
                  Clear
                </v-btn>
              </v-col>
            </v-row>

            <v-alert
              v-if="searchErrorMessage"
              type="error"
              outlined
              dense
              class="mt-3"
              dismissible
              @input="clearSearchError"
            >
              {{ searchErrorMessage }}
            </v-alert>

            <template v-if="hasSearched">
              <v-data-table
                class="mt-3 elevation-1"
                dense
                :headers="headers"
                :items="searchResults"
                :loading="searching"
                hide-default-footer
                disable-pagination
                disable-sort
                item-key="assetId"
              >
                <template v-slot:item.confidence="{ item }">
                  {{ formatConfidence(item.confidence) }}
                </template>

                <template v-slot:no-data>
                  <div class="py-6 text-center grey--text">No results for this query.</div>
                </template>
              </v-data-table>

              <div class="d-flex align-center mt-2" v-if="searchTotalCount > 0">
                <v-btn icon small :disabled="page <= 1 || searching" @click="onPrevPageClick">
                  <v-icon>mdi-chevron-left</v-icon>
                </v-btn>
                <span class="caption grey--text text--darken-1 mx-2">
                  Page {{ page }} of {{ totalPages }} ({{ searchTotalCount }} total)
                </span>
                <v-btn icon small :disabled="page >= totalPages || searching" @click="onNextPageClick">
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>

                <v-select
                  v-model="pageSize"
                  :items="[10, 25, 50]"
                  dense
                  outlined
                  hide-details
                  label="Per page"
                  style="max-width: 110px"
                  class="ml-4"
                  @change="onPageSizeChange"
                ></v-select>
              </div>
            </template>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import PageBar from "../shared/PageBar";

export default {
  name: "ProductSearchAdmin",
  components: {
    PageBar,
  },
  data: () => ({
    successMessage: null,
    searchQuery: "",
    page: 1,
    pageSize: 25,
    hasSearched: false,
    headers: [
      { text: "Identifier", value: "identifier" },
      { text: "Name", value: "name" },
      { text: "Confidence", value: "confidence", width: "120px" },
      { text: "Asset ID", value: "assetId" },
    ],
  }),
  computed: {
    ...mapState("productsearchstate", [
      "reindexing",
      "error",
      "searching",
      "searchError",
      "searchResults",
      "searchTotalCount",
      "statsLoading",
      "statsError",
      "indexStats",
    ]),
    errorMessage() {
      return this.error;
    },
    searchErrorMessage() {
      return this.searchError;
    },
    statsErrorMessage() {
      return this.statsError;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.searchTotalCount / this.pageSize));
    },
    documentCountLabel() {
      if (this.statsLoading) {
        return "Loading...";
      }
      return this.indexStats ? this.indexStats.documentCount.toLocaleString() : "—";
    },
    indexSizeLabel() {
      if (this.statsLoading) {
        return "Loading...";
      }
      if (!this.indexStats) {
        return "—";
      }
      const mb = this.indexStats.storageSizeBytes / (1024 * 1024);
      return `${mb.toFixed(2)} MB`;
    },
  },
  mounted() {
    this.GetIndexStats().catch(() => {
      // Error already surfaced via statsErrorMessage.
    });
  },
  methods: {
    ...mapActions("productsearchstate", ["ReindexProducts", "SearchProducts", "GetIndexStats"]),
    ...mapMutations("productsearchstate", ["SetError", "SetSearchError", "SetStatsError", "ClearSearchResults"]),
    async onIndexClick() {
      this.successMessage = null;

      try {
        const result = await this.ReindexProducts();
        const count = result && typeof result.count === "number" ? result.count : 0;
        this.successMessage = `Indexed ${count} asset${count === 1 ? "" : "s"}.`;
      } catch (e) {
        // Error already surfaced via errorMessage (bound to productsearchstate's error).
      }
    },
    async runSearch() {
      this.hasSearched = true;

      try {
        await this.SearchProducts({ query: this.searchQuery, page: this.page, pageSize: this.pageSize });
      } catch (e) {
        // Error already surfaced via searchErrorMessage.
      }
    },
    onSearchClick() {
      this.page = 1;
      this.runSearch();
    },
    onClearClick() {
      this.searchQuery = "";
      this.page = 1;
      this.hasSearched = false;
      this.ClearSearchResults();
    },
    onPrevPageClick() {
      if (this.page > 1) {
        this.page -= 1;
        this.runSearch();
      }
    },
    onNextPageClick() {
      if (this.page < this.totalPages) {
        this.page += 1;
        this.runSearch();
      }
    },
    onPageSizeChange() {
      this.page = 1;
      this.runSearch();
    },
    formatConfidence(confidence) {
      return typeof confidence === "number" ? `${Math.round(confidence * 100)}%` : "-";
    },
    clearError() {
      this.SetError(null);
    },
    clearSuccess() {
      this.successMessage = null;
    },
    clearSearchError() {
      this.SetSearchError(null);
    },
    clearStatsError() {
      this.SetStatsError(null);
    },
  },
};
</script>

<style scoped>
.product-search-admin-view .stat-tile {
  background: #f6f8fb;
  border: 1px solid #e5e8ef;
}
</style>
